<template>
  <OurPage data-cy="promotions-page" :title="$t('title.promotions._')" gray>
    <DeletePromotionModal
      :show.sync="isShownDeleteModal"
      :promotion-id="promotionId"
      :name="promotionName"
      @confirm="onConfirmDelete"
    />

    <PromotionsList
      :items="promotionsList"
      :headers="promotionsListHeaders"
      :pagination-data="paginationData"
      :is-loading="isLoading"
      :have-permission-edit="getPermission['dealer_incentive.update']"
      :have-permission-delete="getPermission['dealer_incentive.delete']"
      @pageChange="getData"
      @clickPromotion="onClickPromotion"
      @clickEdit="onClickEdit"
      @clickDelete="onClickDelete"
    />

    <template #header-right>
      <div class="buttons">
        <TableColumnsSettings
          v-if="!isMobileDevice && isTableColumnsReady"
          :headers="tableHeaders"
          :table-key="tablePreferenceKey"
          data-cy="promotions-list-columns"
        />

        <OurLink v-if="getPermission['dealer_incentive.create']" :route="addRoute">
          <OurButton :text="$t('button.createPromotion')" />
        </OurLink>
      </div>
    </template>
  </OurPage>
</template>

<script>
import { mapGetters } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import DateService from "@/services/date.service";
import PromotionsList from "@/views/settings/promotions/index/_components/PromotionsList.vue";
import DeletePromotionModal from "@/views/settings/promotions/edit/_components/DeletePromotionModal.vue";
import tableColumnsMixin from "@/mixins/tableColumns.mixin";
import TableColumnsSettings from "@/components/TableColumnsSettings.vue";
import { buildPromotionsListTableHeaders } from "@/views/settings/promotions/index/config/promotionsListTableHeaders";
import { deleteDealerIncentiveMutation } from "@/views/settings/promotions/edit/gql/mutations";
import { dealerIncentiveListQuery } from "@/views/settings/promotions/index/gql/queries";

export default {
  name: "Promotions",

  components: { PromotionsList, DeletePromotionModal, TableColumnsSettings },

  mixins: [apolloMixin, tableColumnsMixin],

  data() {
    return {
      promotionsList: [],
      paginationData: {},
      sorting: {
        column: "createdAt",
        direction: "desc",
      },
      isShownDeleteModal: false,
      promotionId: "",
      promotionName: "",
      isLoading: false,
      tableColumnsId: "promotions-list",
    };
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("user", ["getPermission"]),
    ...mapGetters("promotions", ["getStatusByValue"]),

    addRoute() {
      return { name: "PromotionsAdd" };
    },

    showActionsColumn() {
      return (
        this.getPermission["dealer_incentive.update"] ||
        this.getPermission["dealer_incentive.delete"]
      );
    },

    tableHeaders() {
      return buildPromotionsListTableHeaders({
        t: (key) => this.$t(key),
        showActionsColumn: this.showActionsColumn,
      });
    },

    promotionsListHeaders() {
      if (!this.isTableColumnsReady) {
        return null;
      }

      return this.visibleTableHeaders;
    },
  },

  created() {
    this.getData();
  },

  methods: {
    async getData(page = parseInt(this.$route.query.page) ?? 1) {
      this.isLoading = true;

      try {
        const { dealerIncentiveList } = await this.$get(dealerIncentiveListQuery, {
          page,
          orderBy: this.sorting,
        });

        if (dealerIncentiveList) {
          this.promotionsList = dealerIncentiveList.data.map((promotion) =>
            this.preparePromotion(promotion)
          );
          this.paginationData = dealerIncentiveList.paginatorInfo;
        }
      } finally {
        this.isLoading = false;
      }
    },

    preparePromotion(promotion) {
      const { id, name, createdAt, startsAt, endsAt, status } = promotion;

      return {
        id,
        name,
        createdAt: createdAt ? DateService.dateConverter(createdAt) : "–",
        validityPeriod: this.formatValidityPeriod(startsAt, endsAt),
        status: this.getStatusByValue(status),
        buttons: id,
        page: "PromotionsRanking",
      };
    },

    formatValidityPeriod(startsAt, endsAt) {
      if (!startsAt || !endsAt) {
        return "–";
      }

      const start = DateService.dateConverter(startsAt);
      const end = DateService.dateConverter(endsAt);

      return `${start} – ${end}`;
    },

    onClickPromotion(id) {
      this.$router.push({ name: "PromotionsRanking", params: { id } });
    },

    onClickEdit(id) {
      this.$router.push({ name: "PromotionsEdit", params: { id } });
    },

    onClickDelete(id) {
      this.isShownDeleteModal = true;
      this.promotionId = id;
      this.promotionName = this.promotionsList.find((promotion) => promotion.id === id)?.name || "";
    },

    async onConfirmDelete() {
      const { deleteDealerIncentive } = await this.$post(deleteDealerIncentiveMutation, {
        id: this.promotionId,
      });

      if (deleteDealerIncentive?.id) {
        this.isShownDeleteModal = false;
        await this.getData(this.paginationData.currentPage || 1);
      }
    },
  },
};
</script>

<style scoped lang="postcss">
.buttons {
  @apply flex items-center gap-4;
}
</style>
